import { type FC, useState } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Button } from '../../../../../components/Button/Button'
import { useZocksAuth } from '../../../../../components/ScopedBlock/hooks'
import { Spinner } from '../../../../../components/Spinner/Spinner'
import { useAccount } from '../../../../../common/useAccount'
import { useAppDispatch } from '../../../../../store/hooks'
import { addToast } from '../../../../../store/toast/toastSlice'
import { LOCK_ALL_OVERRIDE } from '../../../../../models/Account'
import { SchedulingEmailAudience } from '../../../../../models/Scheduling'
import { ChooseSchedulingEmailTemplateModal, type ChooseTemplateMode } from './ChooseSchedulingEmailTemplateModal'
import { TemplateButton } from './TemplateButton'
import { type SchedulingEmailChoice, useSchedulingEmailChoices } from './useSchedulingEmailChoices'

const TEMPLATE_LIBRARY_PATH = '/workspace/setup/templates?category=scheduling-emails-clients'

interface EmailTemplatesSetupSectionProps {
  onShowReminders?: () => void
}

interface OpenChoice {
  choice: SchedulingEmailChoice
  mode: ChooseTemplateMode
}

interface ChoiceRowProps {
  choice: SchedulingEmailChoice
  canChoose: boolean
  isLocked: boolean
  isLast: boolean
  onOpen: (mode: ChooseTemplateMode) => void
}

const templateName = ({ node, template }: SchedulingEmailChoice) => template?.name ?? (node ? 'Unavailable template' : 'Zocks default')

const LINK_CLASS = 'd-inline p-0 align-baseline zocks-text-primary text-decoration-underline'

const ChoiceRow: FC<ChoiceRowProps> = ({ choice, canChoose, isLocked, isLast, onOpen }) => {
  const name = templateName(choice)
  return (
    <div className={classNames('d-flex align-items-center gap-4 py-3', { 'border-bottom zocks-border-color-gray-200': !isLast })}>
      <div className='d-flex flex-column flex-grow-1 zocks-gap-0.125'>
        <span className='paragraph-medium fw-semibold zocks-text-gray-900'>{choice.email.label}</span>
        <span className='paragraph-small zocks-text-gray-600'>{choice.email.description}</span>
      </div>
      <div className='col-4 flex-shrink-0 d-flex flex-column align-items-start zocks-gap-0.25'>
        {canChoose ? (
          <TemplateButton className='w-100' name={name} onClick={() => onOpen('choose')} />
        ) : isLocked ? (
          <>
            <TemplateButton className='w-100' name={name} disabled />
            <span className='paragraph-extra-small zocks-text-gray-600'>Managed by your enterprise</span>
          </>
        ) : (
          <Button type='button' className={classNames(LINK_CLASS, 'paragraph-medium fw-semibold')} onClick={() => onOpen('view')}>
            {name}
          </Button>
        )}
      </div>
    </div>
  )
}

interface ChoiceGroupProps {
  audience: SchedulingEmailAudience
  choices: SchedulingEmailChoice[]
  hasAdminScope: boolean
  isGloballyLocked: boolean
  onOpen: (open: OpenChoice) => void
}

const ChoiceGroup: FC<ChoiceGroupProps> = ({ audience, choices, hasAdminScope, isGloballyLocked, onOpen }) => (
  <div className='d-flex flex-column'>
    <span className='paragraph-small fw-bold zocks-text-gray-600 pb-1'>{audience}</span>
    {choices
      .filter((choice) => choice.email.audience === audience)
      .map((choice, index, groupChoices) => {
        const isLocked = isGloballyLocked || choice.node?.isEnterpriseEnforced === true
        return (
          <ChoiceRow
            key={choice.email.slug}
            choice={choice}
            canChoose={hasAdminScope && !isLocked}
            isLocked={hasAdminScope && isLocked}
            isLast={index === groupChoices.length - 1}
            onOpen={(mode) => onOpen({ choice, mode })}
          />
        )
      })}
  </div>
)

export const EmailTemplatesSetupSection: FC<EmailTemplatesSetupSectionProps> = ({ onShowReminders }) => {
  const { choices, isLoading, isError, select } = useSchedulingEmailChoices()
  const { hasScope } = useZocksAuth()
  const { account } = useAccount()
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState<OpenChoice | null>(null)
  const hasAdminScope = hasScope('**', 'iph:admin')
  const isGloballyLocked = account == null || account.enterpriseConfig.lockTemplateOverride === LOCK_ALL_OVERRIDE

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column zocks-gap-0.25'>
        <h4 className='zocks-text-gray-900 mb-0'>Email template setup</h4>
        <div className='paragraph-medium zocks-text-gray-700'>
          Choose which template each email uses. Write and edit templates in the{' '}
          <Link to={TEMPLATE_LIBRARY_PATH} className='zocks-text-primary text-decoration-underline'>
            template library
          </Link>
          .
        </div>
        {hasAdminScope ? null : (
          <div className='paragraph-small zocks-text-gray-600'>Your admin manages scheduling emails for the account. You can view them here.</div>
        )}
      </div>
      {isLoading ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : null}
      {isError ? <div className='paragraph-small zocks-text-gray-600'>Could not load the email templates. Please try again.</div> : null}
      {!isLoading && !isError ? (
        <>
          <div className='d-flex flex-column gap-3'>
            <ChoiceGroup
              audience={SchedulingEmailAudience.CLIENTS}
              choices={choices}
              hasAdminScope={hasAdminScope}
              isGloballyLocked={isGloballyLocked}
              onOpen={setOpen}
            />
            {onShowReminders ? (
              <div className='d-flex align-items-start gap-2 py-3 px-3 border rounded-3 zocks-border-color-gray-200 zocks-bg-color-gray-100 paragraph-small'>
                <i className='bi bi-info-circle' aria-hidden='true' />
                <span>
                  Each reminder has its own email template.{' '}
                  <Button type='button' className={LINK_CLASS} onClick={onShowReminders}>
                    Choose reminder emails
                  </Button>
                </span>
              </div>
            ) : null}
          </div>
          <ChoiceGroup
            audience={SchedulingEmailAudience.ADVISOR}
            choices={choices}
            hasAdminScope={hasAdminScope}
            isGloballyLocked={isGloballyLocked}
            onOpen={setOpen}
          />
        </>
      ) : null}
      {open ? (
        <ChooseSchedulingEmailTemplateModal
          email={open.choice.email}
          currentTemplateId={open.choice.template?.id}
          mode={open.mode}
          onSelect={async (template) => {
            await select(open.choice.email, template)
            dispatch(addToast({ variant: 'success', title: `${open.choice.email.label} emails now use "${template.name}".` }))
          }}
          onHide={() => setOpen(null)}
        />
      ) : null}
    </div>
  )
}
